import { useState } from 'react';
import { X, RotateCcw, AlertTriangle } from 'lucide-react';
import { api } from '../api/client';
import './ResetModal.css';

interface Props {
  sessionName: string;
  onClose: () => void;
  onCompleted?: () => void;
}

const DEFAULT_INSTRUCTION =
  '**Reset angefordert.** Aktualisiere jetzt dein Memory mit allem ' +
  'Wichtigen aus dieser Conversation, bevor deine Chat-History gleich ' +
  'gelöscht wird. Achte auf:\n\n' +
  '- Offene Tasks / Versprechen an User oder andere Agents\n' +
  '- Wichtige Konfigurations-Entscheidungen aus diesem Lauf\n' +
  '- In-flight files / branches / PRs die du nicht vergessen darfst\n' +
  '- Projekt- oder User-Präferenzen die noch nicht im Memory stehen\n\n' +
  'Wenn du fertig bist mit dem Memory-Update, schreib in einer ' +
  'eigenen Zeile genau: RESET OK';

type Phase = 'idle' | 'running' | 'done' | 'error';

interface ResetResult {
  wiped_files: number;
  reset_ok_confirmed: boolean;
  memory_backup: string;
  phases: string[];
  final_state: string;
  error?: string;
}

export function ResetModal({ sessionName, onClose, onCompleted }: Props) {
  const [instruction, setInstruction] = useState(DEFAULT_INSTRUCTION);
  const [maxWait, setMaxWait] = useState(90);
  const [forceCloud, setForceCloud] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [result, setResult] = useState<ResetResult | null>(null);
  const [errorText, setErrorText] = useState('');

  const isCloud = sessionName === 'Cloud';

  const startReset = async () => {
    setPhase('running');
    setErrorText('');
    try {
      const res = await api.sessions.reset(sessionName, {
        instruction,
        max_wait: maxWait,
        force_cloud: isCloud ? forceCloud : undefined,
      });
      setResult(res);
      if (res.error) {
        setPhase('error');
        setErrorText(res.error);
      } else {
        setPhase('done');
        onCompleted?.();
      }
    } catch (e) {
      setPhase('error');
      setErrorText(e instanceof Error ? e.message : 'Unbekannter Fehler');
    }
  };

  return (
    <div className="reset-modal-backdrop" onClick={onClose}>
      <div className="reset-modal" onClick={(e) => e.stopPropagation()}>
        <header className="reset-modal-header">
          <h2>
            <RotateCcw size={18} />
            Reset Agent — {sessionName}
          </h2>
          <button className="reset-modal-close" onClick={onClose}>
            <X size={16} />
          </button>
        </header>

        <div className="reset-modal-warning">
          <AlertTriangle size={14} />
          <span>
            Soft-Wipe: der Agent bekommt deine Anweisung, soll sein Memory updaten,
            danach wird die Chat-History gelöscht und er startet fresh.
            Memory-Files (<code>projects/-/memory/</code>) überleben.
          </span>
        </div>

        {phase === 'idle' && (
          <>
            <label className="reset-modal-label">
              Anweisung an den Agent
              <textarea
                className="reset-modal-textarea"
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                rows={10}
                disabled={phase !== 'idle'}
              />
              <small>
                Default fordert den Agent auf, Tasks/Konfig/References ins
                Memory zu schreiben und mit <code>RESET OK</code> zu bestätigen.
              </small>
            </label>

            <label className="reset-modal-label reset-modal-inline">
              Max. Wartezeit (Sekunden) bis Hard-Wipe
              <input
                type="number"
                min={10}
                max={600}
                step={10}
                className="reset-modal-input"
                value={maxWait}
                onChange={(e) => setMaxWait(Math.max(10, Math.min(600, parseInt(e.target.value) || 90)))}
              />
            </label>

            {isCloud && (
              <label className="reset-modal-label reset-modal-cloud-guard">
                <input
                  type="checkbox"
                  checked={forceCloud}
                  onChange={(e) => setForceCloud(e.target.checked)}
                />
                <span>
                  <strong>Cloud (this session)</strong> — hostet das Federation-Memory.
                  Reset ist standardmäßig blockiert. Anhaken zum Override.
                </span>
              </label>
            )}

            <div className="reset-modal-actions">
              <button className="reset-modal-cancel" onClick={onClose}>
                Abbrechen
              </button>
              <button
                className="reset-modal-submit"
                onClick={startReset}
                disabled={!instruction.trim() || (isCloud && !forceCloud)}
              >
                Reset starten
              </button>
            </div>
          </>
        )}

        {phase === 'running' && (
          <div className="reset-modal-progress">
            <div className="reset-modal-spinner" />
            <div>
              Reset läuft… (Memory-Update → Wait für <code>RESET OK</code> → Wipe → Restart)
            </div>
            <small>
              Kann bis zu {maxWait + 30} Sekunden dauern. Bitte nicht
              schließen.
            </small>
          </div>
        )}

        {phase === 'error' && (
          <div className="reset-modal-result reset-modal-error">
            <strong>Reset abgebrochen — History intakt:</strong>
            <div>{errorText}</div>
            {result?.phases && (
              <pre className="reset-modal-phases">{result.phases.join('\n')}</pre>
            )}
            <div className="reset-modal-actions">
              <button className="reset-modal-cancel" onClick={onClose}>
                Schließen
              </button>
            </div>
          </div>
        )}

        {phase === 'done' && result && (
          <div className="reset-modal-result reset-modal-ok">
            <strong>Reset durch:</strong>
            <ul>
              <li>{result.wiped_files} jsonl-Dateien gelöscht</li>
              <li>
                Memory-Update:{' '}
                {result.reset_ok_confirmed
                  ? `bestätigt durch "RESET OK"`
                  : `Timeout — Memory könnte unvollständig sein`}
              </li>
              {result.memory_backup && (
                <li>
                  Memory-Backup: <code>{result.memory_backup}</code>
                </li>
              )}
              <li>Final-State: <code>{result.final_state}</code></li>
            </ul>
            <details>
              <summary>Alle Phasen</summary>
              <pre className="reset-modal-phases">{result.phases.join('\n')}</pre>
            </details>
            <div className="reset-modal-actions">
              <button className="reset-modal-cancel" onClick={onClose}>
                Schließen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
